import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";

interface Props {
  children: React.ReactNode;
  name: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class PanelErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`[PanelErrorBoundary] Panel "${this.props.name}" crashed:`, error);
    console.error('[PanelErrorBoundary] Error info:', errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card className="border-destructive/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <CardTitle>Panel Crashed: {this.props.name}</CardTitle>
            </div>
            <CardDescription>Error boundary caught an exception</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <div className="font-medium text-sm text-destructive mb-1">Error Message</div>
              <code className="text-xs bg-muted p-2 rounded block whitespace-pre-wrap">
                {this.state.error?.message || 'Unknown error'}
              </code>
            </div>

            {this.state.error?.stack && (
              <div>
                <div className="font-medium text-sm mb-1">Stack Trace (Preview)</div>
                <code className="text-xs bg-muted p-2 rounded block whitespace-pre-wrap max-h-32 overflow-y-auto">
                  {this.state.error.stack.slice(0, 500)}{this.state.error.stack.length > 500 ? '...' : ''}
                </code>
              </div>
            )}

            <div className="text-xs text-muted-foreground">
              This panel has crashed. Other panels on this page may still work correctly.
              Check the browser console for full error details.
            </div>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}
